import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  fetchAllReviews, createReview, updateReview, deleteReview,
  createReply, deleteReply,
} from "../common/api";

const TOUR_COLORS = {
  bts: { bg: 'bg-purple-100', text: 'text-purple-700' },
  dmz: { bg: 'bg-blue-100', text: 'text-blue-700' },
  jeonju: { bg: 'bg-amber-100', text: 'text-amber-700' },
  nami: { bg: 'bg-green-100', text: 'text-green-700' },
  pocheon: { bg: 'bg-pink-100', text: 'text-pink-700' },
  seoul: { bg: 'bg-red-100', text: 'text-red-700' },
  suwon: { bg: 'bg-teal-100', text: 'text-teal-700' },
};

const AVATAR_COLORS = [
  { bg: 'bg-orange-100', text: 'text-orange-600' },
  { bg: 'bg-blue-100', text: 'text-blue-600' },
  { bg: 'bg-green-100', text: 'text-green-600' },
  { bg: 'bg-purple-100', text: 'text-purple-600' },
  { bg: 'bg-pink-100', text: 'text-pink-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
];

function getAvatarColor(userId) {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

const BASE_URL = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 10;
const SLIDE_INTERVAL = 3000; // 3초마다 슬라이드

function maskEmail(email) {
  const [id] = email.split("@");
  const visible = id.slice(0, 2);
  const masked = "*".repeat(Math.max(id.length - 3, 3));
  return `${visible}${masked}`;
}

function authHeader() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function StarRating({ value, onChange, readonly = false }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          className={`text-[20px] transition-colors ${readonly ? "cursor-default" : "cursor-pointer"}`}
        >
          <span className={(hovered || value) >= star ? "text-[#E8876A]" : "text-gray-200"}>★</span>
        </button>
      ))}
    </div>
  );
}

function FlatReviewItem({ review, user,
  editId, editRating, editContent, setEditId, setEditRating, setEditContent,
  handleUpdateReview, handleDeleteReview,
  replyOpen, setReplyOpen, replyInputs, setReplyInputs,
  handleSubmitReply, handleDeleteReply,
  onExpand,
}) {
  const isEditing = editId === review.id;
  const isReplying = !!replyOpen[review.id];

  return (
    <div className="text-left">
      <div className="text-[#E8876A] text-[12px] tracking-[1px]">
        {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
      </div>

      {isEditing ? (
        <div className="mt-2 bg-black/35 backdrop-blur-sm rounded-[14px] p-4 flex flex-col gap-3">
          <StarRating value={editRating} onChange={setEditRating} />
          <textarea
            rows={3}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full px-3 py-2 rounded-[10px] border border-white/20 text-[13px] bg-white/90 outline-none focus:border-[#E8876A] resize-none"
          />
          <div className="flex gap-2">
            <button onClick={() => setEditId(null)} className="flex-1 py-2 rounded-[10px] border border-white/30 text-[12px] text-white hover:bg-white/10">Cancel</button>
            <button onClick={() => handleUpdateReview(review.id)} className="flex-[2] py-2 rounded-[10px] bg-[#E8876A] text-white text-[12px] font-semibold hover:bg-[#D96F52] border-0">Save</button>
          </div>
        </div>
      ) : (
        <p
          onClick={() => onExpand(review)}
          className="mt-2 text-[16px] font-semibold text-white leading-[1.5] cursor-pointer line-clamp-2"
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.45)" }}
        >“{review.content}”</p>
      )}

      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-[12px] text-white/80 m-0">{maskEmail(review.email)} · {review.tour_title}</p>
        {!isEditing && (
          <div className="flex items-center gap-3 shrink-0">
            {user?.userId === review.user_id && (
              <>
                <button
                  onClick={() => { setEditId(review.id); setEditRating(review.rating); setEditContent(review.content); }}
                  className="text-[11px] text-white/70 hover:text-white hover:underline"
                >Edit</button>
                <button onClick={() => handleDeleteReview(review.id)} className="text-[11px] text-white/70 hover:text-white hover:underline">Delete</button>
              </>
            )}
            {user && (
              <button
                onClick={() => setReplyOpen((prev) => ({ ...prev, [review.id]: !prev[review.id] }))}
                className="text-[11px] text-white/70 hover:text-white hover:underline"
              >↩ Reply</button>
            )}
          </div>
        )}
      </div>

      {(review.replies?.length > 0 || isReplying) && (
        <div className="mt-3 bg-black/30 backdrop-blur-sm rounded-[14px] p-3 flex flex-col gap-2">
          {review.replies?.map((reply) => (
            <div key={reply.id} className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                {reply.email[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold text-white/90 m-0">{reply.email.split("@")[0]}</p>
                  <p className="text-[11px] text-white/50 m-0 shrink-0">{timeAgo(reply.created_at)}</p>
                </div>
                <p className="text-[12px] text-white/80 m-0 mt-0.5 leading-relaxed whitespace-pre-wrap">{reply.content}</p>
              </div>
              {user?.userId === reply.user_id && (
                <button onClick={() => handleDeleteReply(reply.id)} className="text-[11px] text-white/60 hover:text-white shrink-0">Delete</button>
              )}
            </div>
          ))}
          {isReplying && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Write a reply..."
                value={replyInputs[review.id] || ""}
                onChange={(e) => setReplyInputs((prev) => ({ ...prev, [review.id]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitReply(review.id)}
                className="flex-1 px-3 py-2 rounded-[10px] border border-white/20 text-[12px] bg-white/90 outline-none focus:border-[#E8876A]"
                autoFocus
              />
              <button onClick={() => handleSubmitReply(review.id)} className="px-3 py-2 rounded-[10px] bg-[#E8876A] text-white text-[12px] font-semibold hover:bg-[#D96F52] border-0">Reply</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AllReviews() {
  const { user } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // 슬라이더
  const [current, setCurrent] = useState(0);
  const [animate, setAnimate] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const [myBookings, setMyBookings] = useState([]);
  const [myReviewedBookingIds, setMyReviewedBookingIds] = useState(new Set());

  const [showForm, setShowForm] = useState(false);
  const [formTourId, setFormTourId] = useState("");
  const [formBookingId, setFormBookingId] = useState("");
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [editId, setEditId] = useState(null);
  const [editRating, setEditRating] = useState(5);
  const [editContent, setEditContent] = useState("");

  const [replyInputs, setReplyInputs] = useState({});
  const [replyOpen, setReplyOpen] = useState({});

  const [expandedReview, setExpandedReview] = useState(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!user) return;
    fetch(`${BASE_URL}/bookings`, { headers: authHeader() })
      .then((r) => r.json())
      .then((b) => setMyBookings(b.filter((x) => x.status !== "cancelled")))
      .catch(console.error);
    refreshMyReviews();
  }, [user]);

  async function refreshMyReviews() {
    if (!user) return;
    fetch(`${BASE_URL}/reviews/my`, { headers: authHeader() })
      .then((r) => r.json())
      .then((data) => setMyReviewedBookingIds(new Set(data.map((r) => r.booking_id))))
      .catch(console.error);
  }

  async function load() {
    setLoading(true);
    try {
      const data = await fetchAllReviews(0, PAGE_SIZE);
      setReviews(data.reviews);
      setAvgRating(data.avgRating);
      setTotal(data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function reload() { await load(); }

  // 자동 슬라이드
  const next = useCallback(() => {
    if (reviews.length === 0) return;
    setAnimate(true);
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % reviews.length);
      setAnimate(false);
    }, 400);
  }, [reviews.length]);

  useEffect(() => {
    if (paused || reviews.length <= 1) return;
    timerRef.current = setInterval(next, SLIDE_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [next, paused, reviews.length]);

  const writableBookings = myBookings.filter((b) => !myReviewedBookingIds.has(b.id));

  async function handleSubmitReview(e) {
    e.preventDefault();
    if (!formTourId) return setFormError("Please select a tour");
    if (!formBookingId) return setFormError("No valid booking found for this tour");
    if (!content.trim()) return setFormError("Please write a review");
    setFormError(null);
    setSubmitting(true);
    try {
      await createReview({ tour_id: formTourId, booking_id: formBookingId, rating, content });
      setShowForm(false);
      setContent(""); setRating(5); setFormTourId(""); setFormBookingId("");
      await refreshMyReviews();
      await reload();
    } catch (err) { setFormError(err.message); }
    finally { setSubmitting(false); }
  }

  async function handleUpdateReview(id) {
    try { await updateReview(id, { rating: editRating, content: editContent }); setEditId(null); await reload(); }
    catch (err) { alert(err.message); }
  }

  async function handleDeleteReview(id) {
    if (!window.confirm("Delete this review?")) return;
    try { await deleteReview(id); await reload(); }
    catch (err) { alert(err.message); }
  }

  async function handleSubmitReply(reviewId) {
    const text = replyInputs[reviewId]?.trim();
    if (!text) return;
    try {
      await createReply(reviewId, text);
      setReplyInputs((prev) => ({ ...prev, [reviewId]: "" }));
      setReplyOpen((prev) => ({ ...prev, [reviewId]: false }));
      await reload();
    } catch (err) { alert(err.message); }
  }

  async function handleDeleteReply(id) {
    if (!window.confirm("Delete this reply?")) return;
    try { await deleteReply(id); await reload(); }
    catch (err) { alert(err.message); }
  }

  const slideProps = {
    user, editId, editRating, editContent,
    setEditId, setEditRating, setEditContent,
    handleUpdateReview, handleDeleteReview,
    replyOpen, setReplyOpen, replyInputs, setReplyInputs,
    handleSubmitReply, handleDeleteReply,
    onExpand: setExpandedReview,
  };

  const review = reviews[current];

  return (
    <section className="py-10 md:py-8">
      <div className="max-w-[520px] mx-auto px-6 md:px-4">

        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-[18px] font-bold text-white m-0" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.4)" }}>Guest Reviews</h2>
            {total > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[#E8876A] text-[14px]">★</span>
                <span className="text-[13px] font-semibold text-white">{avgRating}</span>
                <span className="text-[12px] text-white/70">({total} reviews)</span>
              </div>
            )}
          </div>
          {user && writableBookings.length > 0 && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 text-[13px] font-medium text-white border border-white/50 rounded-full hover:bg-white/10 transition-colors"
            >+ Write a Review</button>
          )}
        </div>

        {/* 리뷰 작성 폼 */}
        {showForm && (
          <form onSubmit={handleSubmitReview} className="mb-8 p-5 bg-white rounded-[20px] border border-gray-200 flex flex-col gap-4">
            <h3 className="text-[15px] font-bold text-gray-800 m-0">Write a Review</h3>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] text-gray-500">Tour</label>
              <select
                value={formBookingId}
                onChange={(e) => {
                  const booking = writableBookings.find((b) => b.id === Number(e.target.value));
                  setFormBookingId(booking?.id ?? "");
                  setFormTourId(booking?.tour_id ?? "");
                }}
                className="px-3 py-2 rounded-[10px] border border-gray-200 text-[13px] text-gray-800 bg-white outline-none focus:border-[#E8876A] focus:ring-2 focus:ring-[#E8876A]/20"
              >
                <option value="">Select a tour</option>
                {writableBookings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title} — {String(b.date).slice(0, 10)} ({b.booking_code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[12px] text-gray-500">Rating</label>
              <StarRating value={rating} onChange={setRating} />
            </div>
            <textarea
              rows={4}
              placeholder="Share your experience..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 rounded-[10px] border border-gray-200 text-[13px] text-gray-800 bg-white outline-none focus:border-[#E8876A] focus:ring-2 focus:ring-[#E8876A]/20 resize-none"
            />
            {formError && <p className="text-[12px] text-red-400 m-0">{formError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormError(null); setFormTourId(""); }}
                className="flex-1 py-2.5 rounded-[12px] border border-gray-200 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors"
              >Cancel</button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-[2] py-2.5 rounded-[12px] bg-[#E8876A] text-white text-[13px] font-semibold hover:bg-[#D96F52] transition-colors disabled:opacity-50 border-0"
              >{submitting ? "Submitting..." : "Submit Review"}</button>
            </div>
          </form>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="h-36 bg-white/15 rounded-[20px] animate-pulse" />
        )}

        {/* 리뷰 없음 */}
        {!loading && reviews.length === 0 && (
          <div className="text-center py-16 text-white/70">
            <p className="text-[32px] mb-2">💬</p>
            <p className="text-[14px]">No reviews yet. Be the first to share your experience!</p>
          </div>
        )}

        {/* 슬라이더 */}
        {!loading && review && (
          <div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="overflow-hidden"
          >
            <div
              style={{
                opacity: animate ? 0 : 1,
                transform: animate ? "translateX(-24px)" : "translateX(0)",
                transition: "opacity 0.4s ease, transform 0.4s ease",
              }}
              className="flex flex-col gap-5"
            >
              <FlatReviewItem key={review.id} review={review} {...slideProps} />
              {(() => {
                if (reviews.length <= 1) return null;
                const nextReview = reviews[(current + 1) % reviews.length];
                if (nextReview.id === review.id) return null;
                return (
                  <>
                    <div className="h-px bg-white/25" />
                    <FlatReviewItem key={nextReview.id} review={nextReview} {...slideProps} />
                  </>
                );
              })()}
            </div>

            {/* 인디케이터 */}
            <div className="flex items-center justify-center gap-4 mt-5">
              <button
                type="button"
                onClick={() => {
                  setAnimate(true);
                  setTimeout(() => {
                    setCurrent((prev) => (prev - 1 + reviews.length) % reviews.length);
                    setAnimate(false);
                  }, 400);
                }}
                className="w-7 h-7 rounded-full border border-white/40 text-white/80 hover:bg-white/10 flex items-center justify-center text-[13px]"
              >‹</button>
              <span className="text-[12px] text-white/70">{current + 1} / {total}</span>
              <button
                type="button"
                onClick={next}
                className="w-7 h-7 rounded-full border border-white/40 text-white/80 hover:bg-white/10 flex items-center justify-center text-[13px]"
              >›</button>
            </div>
          </div>
        )}
      </div>

      {/* 리뷰 전체보기 팝업 */}
      {expandedReview && (
        <div
          onClick={() => setExpandedReview(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[20px] w-full max-w-[480px] max-h-[80vh] overflow-y-auto p-6 relative"
          >
            <button
              type="button"
              onClick={() => setExpandedReview(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-[18px] leading-none"
            >✕</button>

            <span className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full mb-3 ${(TOUR_COLORS[expandedReview.tour_id] ?? { bg: 'bg-gray-100', text: 'text-gray-600' }).bg} ${(TOUR_COLORS[expandedReview.tour_id] ?? { bg: 'bg-gray-100', text: 'text-gray-600' }).text}`}>
              {expandedReview.tour_title}
            </span>
            <div className="flex items-start justify-between gap-2 mb-3 pr-6">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold ${getAvatarColor(expandedReview.user_id).bg} ${getAvatarColor(expandedReview.user_id).text}`}>
                  {expandedReview.email[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-gray-800 m-0">{maskEmail(expandedReview.email)}</p>
                  <p className="text-[11px] text-gray-400 m-0">{timeAgo(expandedReview.created_at)}</p>
                </div>
              </div>
              <StarRating value={expandedReview.rating} readonly />
            </div>
            <p className="text-[14px] text-gray-700 m-0 leading-relaxed whitespace-pre-wrap">{expandedReview.content}</p>

            {expandedReview.replies?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-3">
                {expandedReview.replies.map((reply) => (
                  <div key={reply.id} className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0">
                      {reply.email[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold text-gray-600 m-0">{reply.email.split("@")[0]}</p>
                        <p className="text-[11px] text-gray-400 m-0 shrink-0">{timeAgo(reply.created_at)}</p>
                      </div>
                      <p className="text-[12px] text-gray-600 m-0 mt-0.5 leading-relaxed whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}